import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import './Scrollbar.css';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import 'react-resizable/css/styles.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import styled from 'styled-components';
import { useLocation } from './components/AreaContext';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import TopMenu from './components/TopMenu';
import Login from './components/Login';
import LeftMenu from './components/LeftMenu';
import EventDetail from './components/EventDetail';
import { PROFILE_URL, getJsonFile, DATA_URL } from './config';

// Lazy-loaded components
const GanttWrapper = lazy(() => import('./components/Gantt'));
const List = lazy(() => import('./components/List'));
const Actions = lazy(() => import('./components/Actions'));
const Map = lazy(() => import('./components/Map'));
const DQT = lazy(() => import('./dqt/DQT'));
const Converter = lazy(() => import('./converter/Converter'));

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: black;
  color: white;
`;

const LoadingText = styled.p`
  font-size: 1.0rem;
  margin-bottom: 20px;
`;

const LoadingGif = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 10px;
`;

const App = () => {
  const [user, setUser] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('Reservations');
  const { selectedLocation } = useLocation();
  const [jsonData, setJsonData] = useState(null);
  const [filter, setFilter] = useState('');
  const [clusterIsActiveMap, setClusterIsActiveMap] = useState({});
  const [colorTheme, setColorTheme] = useState('#1a475f');

  // Update page title
  useEffect(() => {
    if (!selectedLocation) return;
    const defaultTitle = 'V-LARA WBC';
    document.title = `${defaultTitle} | ${selectedLocation.toUpperCase()}`;
  }, [selectedLocation]);

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(DATA_URL + `settings/login?t=${Date.now()}`, { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          const map = data.reduce((acc, item) => {
            acc[item.location] = item.clusterIsActive;
            return acc;
          }, {});
          setClusterIsActiveMap(map);

          const selectedItem = data.find(item => item.location === selectedLocation);
          if (selectedItem) setColorTheme(selectedItem.colorTheme);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [selectedLocation]);

  // Fetch JSON data
  useEffect(() => {
    const fetchJson = async () => {
      try {
        const url = getJsonFile(selectedLocation);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to load JSON data');
        const data = await response.json();
        setJsonData(data);
      } catch (err) {
        console.error('Failed to fetch JSON data:', err);
        setJsonData(null);
      }
    };
    if (selectedLocation) fetchJson();
  }, [selectedLocation]);

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${PROFILE_URL}?location=${encodeURIComponent(selectedLocation)}&t=${Date.now()}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setView(data.view);
      } else {
        setUser(null);
        setView(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedLocation) return;
    fetchUserProfile(); // Initial load
    const intervalId = setInterval(fetchUserProfile, 300000); // 5 min
    return () => clearInterval(intervalId);
  }, [selectedLocation]);

  // Update color theme
  useEffect(() => {
    document.documentElement.style.setProperty('--color-theme', colorTheme);
  }, [colorTheme]);

  // Loading screen
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingText>
          Loading
          <LoadingGif src="images/ajax-loader.gif" alt="Loading" />
        </LoadingText>
      </LoadingContainer>
    );
  }

  // If not logged in
  if (!user) return <Login clusterIsActiveMap={clusterIsActiveMap} />;

  const handlePageSelect = (page) => {
    setActivePage(page);
  };

  return (
    <Router>
      <Suspense fallback={
        <LoadingContainer>
          <LoadingText>
            Loading
            <LoadingGif src="images/ajax-loader.gif" alt="Loading" />
          </LoadingText>
        </LoadingContainer>
      }>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <LeftMenu view={view} jsonData={jsonData} userData={user} />
                <PanelGroup direction="horizontal" style={{ marginLeft: '60px', width: 'auto' }}>
                  <Panel defaultSize={65} minSize={30}>
                    <PanelGroup direction="vertical">
                      <Panel defaultSize={60} minSize={30}>
                        <TopMenu
                          user={user}
                          view={view}
                          onPageSelect={handlePageSelect}
                          jsonData={jsonData}
                          filter={filter}
                          setFilter={setFilter}
                        />
                        {activePage === 'Reservations' ? <List filter={filter} user={user} /> : <GanttWrapper />}
                      </Panel>
                      <PanelResizeHandle className="custom-resize-handle-horizontal" />
                      <Panel defaultSize={40} minSize={20}>
                        <Actions user={user} view={view} filter={filter} />
                      </Panel>
                    </PanelGroup>
                  </Panel>
                  <PanelResizeHandle className="custom-resize-handle-vertical" />
                  <Panel defaultSize={35} minSize={20}>
                    <Map view={view} jsonData={jsonData} />
                  </Panel>
                </PanelGroup>
              </>
            }
          />
          <Route path="/dqt" element={<><LeftMenu view={view} /><DQT /></>} />
          <Route path="/converter" element={<><LeftMenu view={view} /><Converter /></>} />
          <Route path="/events/:id" element={<EventDetail />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;